// TODO(somewhatabstract, FEI-6139): Need to investigate why this file causes
// the test coverage to fail annotating the code. Ignoring the file for test
// coverage for now so that we can do coverage checks on tests.
/* istanbul ignore file */
/**
 * Learning Time v2 calculation logic.
 *
 * For details on overall logic and terminology used herein, see ADR #152.
 *
 * TODO(slim): Break this file up into pieces.
 */
import {applyMiddleware, createStore, type Store as ReduxStore} from "redux";
import thunkMiddleware from "redux-thunk";

import {DeployInfo, RequestInfo} from "@khan/shared-core";
import {createCompose} from "@khan/util-redux-create-compose";

import * as api from "./api.ts";
import * as enums from "./enums.ts";
import {
    type LearningTimeContent,
    type LearningTimeInfo,
    type LearningTimeTestPrep,
    type MajorConversion,
    type MajorConversionStub,
    type MinorConversion,
    type PartialLearningTimeInfo,
    type Store,
    type ThunkAction,
} from "./types.ts";
import {
    getConversionForUrl,
    INITIAL_URL_CACHE,
    updateUrlCache,
    type URLCache,
} from "./url-cache.ts";
import {updateLearningTimeInfo} from "./util.ts";

/*************************************************
 * State
 ************************************************/

interface State {
    // Last major conversion recorded.
    // Extras will be used for subsequent continuations and terminations.
    lastMajorConversion: MajorConversion | null | undefined;
    // Event timestamp for the last-recorded conversion of ANY kind (major,
    // minor, or continuation).
    // May be defined when `last` is not, when the first conversion fired
    // is a continuation or (unlikely in practice, but possible in theory)
    // a termination event.
    lastTimestamp: number | null | undefined;
    // Associates URLs to the last major conversion fired on that URL.
    urls: URLCache;
}

export const INITIAL_STATE: State = {
    lastMajorConversion: null,
    lastTimestamp: null,
    urls: INITIAL_URL_CACHE,
};

/*************************************************
 * Actions
 ************************************************/

type Action =
    | {
          type: "MAJOR_CONVERSION";
          timestamp: number;
          conversion: MajorConversion;
      }
    | {
          type: "CONTINUATION_CONVERSION";
          timestamp: number;
      }
    | {
          type: "TERMINATION_CONVERSION";
          timestamp: number;
      };

export const ActionType = {
    MAJOR_CONVERSION: "MAJOR_CONVERSION",
    CONTINUATION_CONVERSION: "CONTINUATION_CONVERSION",
    TERMINATION_CONVERSION: "TERMINATION_CONVERSION",
} as const;

// Names of BigBingo conversions used for Learning Time infrastructure.
// Should be listed in _SOURCE_CONVERSIONS in bigbingo/config.py.
export const BigBingoConversion = {
    CONTINUATION_CONVERSION: "continuation_event",
    TERMINATION_CONVERSION: "termination_event",
} as const;

export const actions = {
    markMajorConversions(
        data: ReadonlyArray<MajorConversionStub>,
        learningTimeInfo?: PartialLearningTimeInfo,
    ): ThunkAction<Action, State> {
        const required = getTopLevelClobberedFields();

        // If partial annotations were passed in, extend them with required top-
        // level fields. Otherwise, just use the top-level fields alone.
        const annotations = learningTimeInfo
            ? // Ensure all required annotation fields are filled. Only update
              // content.* and testPrep.* if they're at least partially specified.
              updateLearningTimeInfo(learningTimeInfo, {
                  ...required,
                  ...(learningTimeInfo.content &&
                      (getContentRequiredFields(
                          learningTimeInfo.content,
                      ) as any)),
                  ...(learningTimeInfo.testPrep &&
                      getTestPrepRequiredFields(learningTimeInfo.testPrep)),
              } as LearningTimeInfo)
            : required;

        return (dispatch: unknown) =>
            // @ts-expect-error - TS2571 - Object is of type 'unknown'.
            dispatch(markAnnotatedConversions(data, annotations));
    },

    markMinorConversions(
        data: ReadonlyArray<MinorConversion>,
    ): ThunkAction<Action, State> {
        return (dispatch: unknown, getState: unknown) => {
            // Look up the last major conversion for the current debugging URL.
            // @ts-expect-error - TS2571 - Object is of type 'unknown'.
            const {urls} = getState();
            const lastConversionForUrl = getConversionForUrl(
                urls,
                statefulGlobals.getCurrentUrl(),
            );

            // Ensure all required annotation fields are filled.
            //
            // NOTE(slim): We do not get fresh fields for `content.*` or
            // `testPrep.*`because we want to retain the old structs, which are
            // pointers to the topic tree and/or TestPrep activities at the
            // time of the previous major conversion at this URL.
            //
            // In practice this is unlikely to matter unless a content publish
            // has taken place since then, but in principle this is the correct
            // behavior.
            const withRequired = {
                ...getTopLevelClobberedFields(),
                ...(lastConversionForUrl &&
                    lastConversionForUrl.learningTimeInfo),
            } as const;

            // @ts-expect-error - TS2571 - Object is of type 'unknown'.
            return dispatch(markAnnotatedConversions(data, withRequired));
        };
    },

    markContinuationConversion(
        timestamp: number,
        forceConversion: boolean,
    ): ThunkAction<Action, State> {
        // @ts-expect-error - TS2322 - Type '(dispatch: unknown, getState: unknown) => Promise<void> | Promise<Response | null | undefined>' is not assignable to type 'ThunkAction<Action, State>'.
        return (dispatch: unknown, getState: unknown) => {
            // @ts-expect-error - TS2571 - Object is of type 'unknown'.
            const {lastTimestamp, lastMajorConversion} = getState();

            // Check whether any conversion has been sent in the past interval.
            if (
                !forceConversion &&
                !canSendContinuation(lastTimestamp, timestamp)
            ) {
                return Promise.resolve();
            }

            // @ts-expect-error - TS2571 - Object is of type 'unknown'.
            dispatch({
                type: ActionType.CONTINUATION_CONVERSION,
                timestamp,
            });

            const annotated = {
                conversion: BigBingoConversion.CONTINUATION_CONVERSION,
                timestamp,
                ...(lastMajorConversion
                    ? {
                          learningTimeInfo:
                              lastMajorConversion.learningTimeInfo,
                      }
                    : null),
            } as const;

            return api.markConversionsWithExtras([annotated]);
        };
    },

    markTerminationConversion(timestamp: number): ThunkAction<Action, State> {
        return (dispatch: unknown, getState: unknown) => {
            // @ts-expect-error - TS2571 - Object is of type 'unknown'.
            const {lastMajorConversion} = getState();

            // @ts-expect-error - TS2571 - Object is of type 'unknown'.
            dispatch({
                type: ActionType.TERMINATION_CONVERSION,
                timestamp,
            });

            const annotated = {
                conversion: BigBingoConversion.TERMINATION_CONVERSION,
                timestamp,
                ...(lastMajorConversion
                    ? {
                          learningTimeInfo:
                              lastMajorConversion.learningTimeInfo,
                      }
                    : null),
            } as const;

            return api.markConversionsWithExtras([annotated]);
        };
    },
} as const;

/**
 * Dispatches major conversions with Learning Time annotations, and makes a
 * network request.
 *
 * It is called by the markMajorConversions and markMinorConversions action
 * creators, and not exposed to external consumers.
 */
const markAnnotatedConversions = (
    data: ReadonlyArray<MajorConversionStub>,
    learningTimeInfo: LearningTimeInfo,
): ThunkAction<Action, State> => {
    if (!data) {
        // NOTE(kevinb): Sometimes the tests can get into a situation where
        // `data` is undefined.
        // @ts-expect-error - TS2322 - Type '(dispatch: unknown) => Promise<void>' is not assignable to type 'ThunkAction<Action, State>'.
        return async (dispatch: unknown) => {};
    }
    const conversions = data.map((conversion): MajorConversion => {
        return {
            ...conversion,
            learningTimeInfo,
        };
    });
    return (dispatch: unknown) => {
        for (const conversion of conversions) {
            // @ts-expect-error - TS2571 - Object is of type 'unknown'.
            dispatch({
                type: ActionType.MAJOR_CONVERSION,
                timestamp: conversion.timestamp,
                conversion,
            });
        }
        return api.markConversionsWithExtras(conversions);
    };
};

/*************************************************
 * Utils
 ************************************************/

const MS_PER_SECOND = 1000;

// Given a delay in milliseconds, returns a predicate that takes two millisecond
// timestamps and checks whether the `delay` has elapsed.
const hasDelayElapsed = (delay: number) => (earlier: number, later: number) =>
    later - earlier >= delay;

// Maximum interval at which continuations are sent to the server.
export const CONTINUATION_GAP_MS = 20 * MS_PER_SECOND;
const hasContinuationGapElapsed = hasDelayElapsed(CONTINUATION_GAP_MS);

/**
 * Checks whether the continuation should be sent to the server.
 */
const canSendContinuation = (
    lastTimestamp: State["lastTimestamp"],
    continuationTimestamp: number,
) =>
    // If this is the first continuation being sent, we can send it.
    lastTimestamp == null ||
    hasContinuationGapElapsed(lastTimestamp, continuationTimestamp);

const getTopLevelClobberedFields = (): {
    localTimeOffsetSeconds: {
        offsetSeconds: number;
    };
    urlForDebugging: string;
    kaLocale: string;
} => {
    // A few things to note about these fields:
    // - They are top-level fields in the LearningTimeInfo definition
    // - They are required
    // - They are clobbered each time we dispatch a new set of conversions, in order
    //   to keep e.g. the debugging URL up-to-date.
    return {
        localTimeOffsetSeconds: {
            // NOTE(slim): JavaScript's timezone offsets are funky.
            //
            // - Offsets are provided as the difference in minutes (NOT seconds)
            // - Offsets are the difference from local time to UTC. This means
            //   the sign is +1 if local time is behind UTC, and -1 if local
            //   time is ahead. This is backwards from what you'd expect!
            //   For PST, which is UTC-8, getTimezoneOffset() returns 480.
            //
            // Consequently, we convert to seconds and correct the sign. The
            // result should be that
            //     SERVER_START_TIME + OFFSET_SECONDS = LOCAL_TIME.
            //
            // For more info, see:
            // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/getTimezoneOffset#Description
            offsetSeconds: -1 * statefulGlobals.getTimezoneOffsetMinutes() * 60,
        },
        urlForDebugging: statefulGlobals.getCurrentUrl(),
        kaLocale: RequestInfo.kaLocale,
    };
};

const getContentRequiredFields = (
    partialContent: Partial<LearningTimeContent>,
): {
    content: LearningTimeContent;
} => {
    const content = {
        // Always generate a new commit SHA.
        contentCommitSha: statefulGlobals.getContentCommitSha(),

        // If there is a kind specified, use that. Otherwise, fall back to Topic.
        kind: partialContent.kind || enums.ContentKind.Topic,
    } as const;
    return {content};
};

const getTestPrepRequiredFields = (
    partialTestPrep: Partial<LearningTimeTestPrep>,
): {
    testPrep: LearningTimeTestPrep;
} => {
    const testPrep = {
        // `examGroup` is a required field, so we need to set a value if one
        // isn't already defined.
        // In practice, `examGroup` should always be defined by the TestPrep
        // product. However, we use it as the default value if none is provided,
        // in order to prove to TypeScript that merging a set of
        // Partial<LearningTimeTestPrep> structs can produce a complete
        // LearningTimeTestPrep struct.
        examGroup:
            partialTestPrep.examGroup || enums.ExamGroup.INVALID_EXAM_GROUP,
    } as const;
    return {testPrep};
};

export const statefulGlobals = {
    getCurrentUrl(): string {
        // We don't allow marking conversions in learning-time/api.js exported
        // functions during SSR. So this will not be accessed during SSR.
        /* eslint-disable-next-line no-restricted-syntax
           -- accessing the location here by other means is difficult */
        return window.location.href;
    },
    getTimezoneOffsetMinutes(): number {
        return new Date().getTimezoneOffset();
    },
    getContentCommitSha(): string {
        return DeployInfo.commitSHA;
    },
} as const;

/*************************************************
 * Reducer
 ************************************************/

const reducer = (state: State = INITIAL_STATE, action: Action): State => {
    switch (action.type) {
        case ActionType.MAJOR_CONVERSION: {
            // Cache the conversion so we can reuse its extras for subsequent
            // continuation and termination conversions.
            const {conversion, timestamp} = action;
            return {
                ...state,
                lastMajorConversion: conversion,
                lastTimestamp: timestamp,
                urls: updateUrlCache(state.urls, conversion),
            };
        }
        case ActionType.CONTINUATION_CONVERSION: {
            // Save the timestamp so that we don't send another continuation
            // until the CONTINUATION_GAP has elapsed.
            const {timestamp} = action;
            return {
                ...state,
                lastTimestamp: timestamp,
            };
        }
        case ActionType.TERMINATION_CONVERSION: {
            // Save the timestamp so that we don't send a continuation
            // until the CONTINUATION_GAP has elapsed.
            const {timestamp} = action;
            return {
                ...state,
                lastTimestamp: timestamp,
            };
        }
        default: {
            // Exhaustiveness check.
            const _: never = action;
            return state;
        }
    }
};

/*************************************************
 * Store
 ************************************************/

export type LearningTimeStore = Store<Action, State>;

const compose = createCompose({
    reduxDevtoolsOptions: {
        name: "Learning Time",
        // @ts-expect-error - TS2322 - Type '{ name: string; actionSanitizer: (action: Action) => { type: "CONTINUATION_CONVERSION"; timestamp: number; } | { type: "TERMINATION_CONVERSION"; timestamp: number; } | { type: string; timestamp: number; conversion: Readonly<...>; }; }' is not assignable to type '{ name: string; }'.
        actionSanitizer: (action: Action) => {
            return action.type === "MAJOR_CONVERSION"
                ? // Show major conversion identifiers in the Redux DevTools
                  // sidebar, rather than just "MAJOR_CONVERSION".
                  {...action, type: action.conversion.conversion}
                : // Pass continuation and termination conversions unchanged.
                  action;
        },
    },
});

export type Dispatch = (
    action: Action | ThunkAction<Action, State> | PromiseAction | Array<Action>,
) => any;
export type GetState = () => State;
// export type ThunkAction = (dispatch: Dispatch, getState: GetState) => any;
export type PromiseAction = Promise<Action>;

// NOTE(slim): This function is ONLY exported to create test stores.
// Consumers should always interact with the singleton instead using BigBingo methods.
// eslint-disable-next-line camelcase -- used indicate that this is for testing purposes only
export const createAnalyticsStore_DO_NOT_CALL_EXTERNALLY = (
    initialState: State,
    // @ts-expect-error - TS2707 - Generic type 'Store<S, A>' requires between 0 and 2 type arguments. | TS2558 - Expected 4 type arguments, but got 3.
): ReduxStore<State, Action, Dispatch> =>
    // @ts-expect-error - TS2558
    createStore<State, Action, Dispatch>(
        reducer,
        initialState,
        compose(applyMiddleware(thunkMiddleware)),
    );

// @ts-expect-error - TS2707 - Generic type 'Store<S, A>' requires between 0 and 2 type arguments.
export const store: ReduxStore<State, Action, Dispatch> =
    createAnalyticsStore_DO_NOT_CALL_EXTERNALLY(INITIAL_STATE);
